/**
 * H3.2 — Kund 360 Dashboard
 * Route: /admin/clients/:client_id
 *
 * Per-client overview that unifies runtime, blueprint, presentation,
 * actions, leads, monitor, and onboarding origin into a single product
 * surface. Designed to be the new primary admin entry point for any
 * client — replacing the fragmented "go to Blueprints → click client",
 * "go to Leads → filter by client", etc workflow.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  ArrowLeft, Wrench, ChevronRight, ExternalLink, Mail, MessageCircle,
  Sparkles, Settings, Activity, RefreshCw, Loader2, CheckCircle2,
  AlertCircle, XCircle, ShieldAlert, ChevronDown, Pencil, Globe, Save,
  Copy, Trash2, Beaker
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";
import ApprovedActionsManager from "./ApprovedActionsManager";
import ChatbotLeads from "./ChatbotLeads";
import { EditClientModal, SilentLeadsList, WidgetPreviewModal } from "./ClientsManager";
import { CatalogPanel } from "./CatalogPanel";
import { BlueprintEvidencePanel } from "./BlueprintEvidencePanel";
import { AdminError, AdminLoading } from "./AdminStateViews";
import { Database } from "lucide-react";
import { kundfas as kundfasMeta, widgetStatus, runtimeState } from "./clients/statusHelpers";

// Kund 360 — kundens enda arbetsyta. Åtta huvudflikar i arbetsorderns
// ordning. `actions` är inte en egen flik längre: den nås som sektion
// under Avancerat (?tab=advanced&section=actions), med kompatibilitetsalias
// ?tab=actions.
export const TABS = [
  { id: "overview",   label: "Översikt",                 icon: Wrench },
  { id: "knowledge",  label: "AI-agent & kunskap",       icon: ShieldAlert },
  { id: "catalog",    label: "Produktdata",              icon: Database },
  { id: "appearance", label: "Widget & utseende",        icon: Sparkles },
  { id: "testmon",    label: "Test",                     icon: Activity },
  { id: "leads",      label: "Leads & konversationer",   icon: Mail },
  { id: "portal",     label: "Kundportal",               icon: MessageCircle },
  { id: "advanced",   label: "Avancerat",                icon: Settings },
];

// Fas 3 — admin-styrning av kundportalen per kund. Kallar /api/admin/portal/*
// (staff). Frikopplat från AI-agentens runtime.
function PortalTab({ clientId, getAuthHeaders }) {
  const API = process.env.REACT_APP_BACKEND_URL || "";
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgIsError, setMsgIsError] = useState(false);

  // Verkligt separata tillstånd: loading / error / (in)aktiverad. Läser
  // alltid tillbaka efter lyckad mutation. Formulärdata rensas ENDAST vid
  // lyckad inbjudan — aldrig vid fel.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/portal/${encodeURIComponent(clientId)}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      setStatus(await res.json());
    } catch (e) {
      setError(String(e.message || e));
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [API, clientId, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    try {
      const action = status?.portal_enabled ? "disable" : "enable";
      const res = await fetch(`${API}/api/admin/portal/${encodeURIComponent(clientId)}/${action}`, { method: "POST", headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      toast.success(action === "enable" ? "Portalen aktiverad" : "Portalen inaktiverad");
      await load();
    } catch (e) {
      toast.error(`Kunde inte ändra portalstatus: ${e.message}`);
    } finally { setBusy(false); }
  };

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(""); setMsgIsError(false);
    try {
      const res = await fetch(`${API}/api/admin/portal/${encodeURIComponent(clientId)}/invite`, {
        method: "POST", headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ email, name }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Bevara formulärdata vid fel så admin inte tappar inmatningen.
        setMsg(d.detail || d.error || `Kunde inte bjuda in (status ${res.status})`);
        setMsgIsError(true);
        return;
      }
      setMsg(d.message || "Inbjudan skickad");
      setMsgIsError(false);
      setEmail(""); setName("");
      await load();
    } catch (e) {
      setMsg(`Nätverksfel: ${e.message}`);
      setMsgIsError(true);
    } finally { setBusy(false); }
  };

  const revoke = async (userId) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/portal/${encodeURIComponent(clientId)}/users/${userId}`, { method: "DELETE", headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      toast.success("Åtkomst återkallad");
      await load();
    } catch (e) {
      toast.error(`Kunde inte återkalla: ${e.message}`);
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12" data-testid="portal-loading"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-xl p-8 text-center" data-testid="portal-error">
        <AlertCircle className="w-9 h-9 text-red-400 mx-auto mb-3" />
        <p className="text-sm text-gray-600 mb-4">Kunde inte läsa portalstatus ({error}).</p>
        <Button size="sm" onClick={load} data-testid="portal-retry"><RefreshCw className="w-4 h-4 mr-1" /> Försök igen</Button>
      </div>
    );
  }

  const enabled = !!status?.portal_enabled;
  return (
    <div className="space-y-6" data-testid="portal-admin-tab">
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-5 gap-3 flex-wrap">
        <div>
          <div className="font-semibold text-gray-900">Kundportal</div>
          <div className="text-sm text-gray-500">
            Status: <span className={enabled ? "text-emerald-600 font-medium" : "text-gray-500"} data-testid="portal-status">{enabled ? "Aktiverad" : "Inaktiverad"}</span>
            {" "}· Portalen är ett tillval och påverkar inte AI-agenten.
          </div>
        </div>
        <button onClick={toggle} disabled={busy} data-testid="portal-toggle-btn"
          className={`px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 ${enabled ? "bg-amber-100 text-amber-800" : "bg-[#255E69] text-white"}`}>
          {busy ? "…" : enabled ? "Inaktivera portal" : "Aktivera portal"}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="font-semibold text-gray-900 mb-3">Bjud in användare</div>
        <form onSubmit={invite} className="flex flex-col sm:flex-row gap-2">
          <input type="email" required placeholder="kund@epost.se" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="portal-invite-email" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Namn (valfritt)" value={name} onChange={(e) => setName(e.target.value)}
            data-testid="portal-invite-name" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <button type="submit" disabled={busy || !enabled} data-testid="portal-invite-btn"
            className="bg-[#255E69] text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">Bjud in</button>
        </form>
        {!enabled && <p className="text-xs text-amber-600 mt-2">Aktivera portalen först för att bjuda in användare.</p>}
        {msg && <p className={`text-xs mt-2 ${msgIsError ? "text-red-600" : "text-emerald-600"}`} data-testid="portal-invite-msg">{msg}</p>}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="font-semibold text-gray-900 mb-3">Användare med åtkomst</div>
        <div className="divide-y divide-gray-50" data-testid="portal-users-list">
          {(status?.users || []).filter((u) => u.status === "active").length === 0 && (
            <div className="text-sm text-gray-400 py-2">Inga aktiva användare ännu.</div>
          )}
          {(status?.users || []).map((u) => (
            <div key={u.user_id} className="flex items-center justify-between py-2.5">
              <div>
                <div className="text-sm font-medium text-gray-800">{u.name || u.email}</div>
                <div className="text-xs text-gray-500">{u.email} · {u.status === "active" ? "aktiv" : u.status}</div>
              </div>
              {u.status === "active" && (
                <button onClick={() => revoke(u.user_id)} disabled={busy} data-testid={`portal-revoke-${u.email}`}
                  className="text-xs text-red-600 hover:underline">Återkalla åtkomst</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function StatusBadge({ value, palette = {} }) {
  const defaults = {
    published:       { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
    approved:        { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
    draft:           { bg: "bg-slate-50",   text: "text-slate-600",   border: "border-slate-200" },
    review_required: { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200" },
    archived:        { bg: "bg-gray-100",   text: "text-gray-500",    border: "border-gray-200" },
    suggested:       { bg: "bg-slate-50",   text: "text-slate-600",   border: "border-slate-200" },
    disabled:        { bg: "bg-gray-100",   text: "text-gray-500",    border: "border-gray-200" },
  };
  const p = { ...defaults, ...palette }[value] || { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-200" };
  return (
    <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${p.bg} ${p.text} ${p.border}`}>
      {value || "—"}
    </span>
  );
}

// H3.3 — Source-of-truth-pill: LIVE / UNDERLAG / FALLBACK
// LIVE     = datat används av aktiv runtime just nu i chatten.
// UNDERLAG = förberett för framtida runtime (t.ex. blueprint) — påverkar
//            inte chatten idag.
// FALLBACK = kvar som säkerhetsnät om aktiv runtime rullas tillbaka.
// Denna klassifikation är rent presentationslogik och driver INGEN
// runtime/lead-capture/scope-eller LLM-beteende.
const SOURCE_STATE_META = {
  live:      { label: "LIVE",     bg: "bg-emerald-50",  text: "text-emerald-700", border: "border-emerald-300", ring: "ring-emerald-200", hint: "Används av aktiv runtime just nu." },
  underlag:  { label: "UNDERLAG", bg: "bg-sky-50",      text: "text-sky-700",     border: "border-sky-300",     ring: "ring-sky-200",     hint: "Förberett — påverkar inte chatten idag." },
  fallback:  { label: "FALLBACK", bg: "bg-amber-50",    text: "text-amber-700",   border: "border-amber-300",   ring: "ring-amber-200",   hint: "Säkerhetsnät om aktiv runtime rullas tillbaka." },
};

function SourceStateBadge({ state, testid, className = "" }) {
  const meta = SOURCE_STATE_META[state] || SOURCE_STATE_META.underlag;
  return (
    <span
      title={meta.hint}
      data-testid={testid || `source-state-${state}`}
      data-source-state={state}
      className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-wider rounded-full px-2 py-0.5 border ${meta.bg} ${meta.text} ${meta.border} ${className}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${
        state === "live" ? "bg-emerald-500" : state === "underlag" ? "bg-sky-500" : "bg-amber-500"
      }`} />
      {meta.label}
    </span>
  );
}


// Gemensam kundheader — alltid synlig, över alla flikar. Visar aktuell
// kund tydligt. Vid saknade kundmetadata visas clientId som reservidentitet
// (aldrig föregående kunds namn). Runtime visas sanningsenligt via
// runtime_source; kan vi inte läsa status visas "Kunde inte kontrollera".
function Kund360Header({ clientId, data, onBack, onRefresh, refreshing }) {
  const client = data?.client;
  const runtime = data?.runtime;
  const present = !!(client && client.present);
  const name = (present && client.name) ? client.name : clientId;
  const domain = present ? (client.company_url || client.allowed_domain || "") : "";
  const contact = present ? (client.email || "") : "";
  const phase = present ? kundfasMeta(client.demo?.status) : null;
  const widget = present ? widgetStatus(!!client.active) : null;
  const rt = runtime
    ? runtimeState(runtime.active_runtime, runtime.runtime_health)
    : { label: "Kunde inte kontrollera", cls: "bg-red-50 border-red-200 text-red-700" };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4" data-testid="kund360-header">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900" data-testid="kund360-back">
          <ArrowLeft className="w-4 h-4" /> Till Kunder
        </button>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} data-testid="kund360-refresh">
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 break-words" data-testid="kund360-name">{name}</h1>
          <div className="flex items-center gap-x-3 gap-y-1 mt-1.5 flex-wrap text-xs text-gray-500">
            {!present && (
              <code className="font-mono px-1.5 py-0.5 bg-gray-100 rounded text-gray-600" data-testid="kund360-fallback-id">{clientId}</code>
            )}
            {domain && <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" />{domain}</span>}
            {contact && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{contact}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {phase && (
            <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${phase.cls}`} data-testid="kund360-phase">{phase.label}</span>
          )}
          {widget && (
            <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${widget.cls}`} data-testid="kund360-widget">{widget.label}</span>
          )}
          <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${rt.cls}`} data-testid="kund360-runtime">{rt.label}</span>
        </div>
      </div>
    </div>
  );
}

function SetupRow({ label, status, detail, tone = "neutral", onClick, testid }) {
  const toneCls = {
    ok: "text-emerald-700",
    warn: "text-amber-700",
    error: "text-red-700",
    neutral: "text-gray-500",
  }[tone] || "text-gray-500";
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-center justify-between gap-3 py-3 px-4 hover:bg-gray-50 transition-colors"
      data-testid={testid}
    >
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        {detail && <div className="text-xs text-gray-500 mt-0.5">{detail}</div>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-xs font-semibold ${toneCls}`}>{status}</span>
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </div>
    </button>
  );
}

// Modulnivå — får INTE definieras inne i OverviewTab (då remountas inputen
// och tappar fokus vid varje tangenttryck).
function OvField({ label, k, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={`overview-field-${k}`}
      />
    </div>
  );
}

// Kompakt Översikt: redigerbara grunduppgifter, Widget På/Pausad-kontroll,
// kundfas och en kort uppsättningsstatus (en länk per område). Ersätter den
// tidigare 10-stegs pipelinen + snabbkorten. Ingen LIVE/UNDERLAG/FALLBACK-
// pill och ingen runtime-koppling för presentation/actions (arbetsorder §3).
function OverviewTab({ data, clientId, onTab, getAuthHeaders, onReload }) {
  const client = data?.client || {};
  const blueprint = data?.blueprint || {};
  const [form, setForm] = useState({
    name: client.name || "",
    email: client.email || "",
    industry: client.industry || "",
    city: client.city || "",
    company_url: client.company_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [togglingWidget, setTogglingWidget] = useState(false);

  useEffect(() => {
    setForm({
      name: client.name || "",
      email: client.email || "",
      industry: client.industry || "",
      city: client.city || "",
      company_url: client.company_url || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.client_id, client.name, client.email, client.industry, client.city, client.company_url]);

  const patchClient = async (body) => {
    // Blockera sparning tills laddad kundidentitet matchar routens clientId.
    if (!data || data.client_id !== clientId) {
      throw new Error("Kundidentitet matchar inte routen — läser om");
    }
    const res = await fetch(`${API_URL}/api/admin/client/${encodeURIComponent(clientId)}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t.slice(0, 160) || `Status ${res.status}`);
    }
    return res.json();
  };

  const saveBasics = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Namn och e-post krävs");
      return;
    }
    setSaving(true);
    try {
      await patchClient({
        name: form.name.trim(),
        email: form.email.trim(),
        industry: form.industry.trim(),
        city: form.city.trim(),
        company_url: form.company_url.trim(),
      });
      toast.success("Grunduppgifter sparade");
      if (onReload) await onReload();
    } catch (e) {
      toast.error(`Kunde inte spara: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleWidget = async () => {
    setTogglingWidget(true);
    try {
      await patchClient({ active: !client.active });
      toast.success(!client.active ? "Widget aktiverad" : "Widget pausad");
      if (onReload) await onReload();
    } catch (e) {
      toast.error(`Kunde inte ändra widgetstatus: ${e.message}`);
    } finally {
      setTogglingWidget(false);
    }
  };

  const phase = kundfasMeta(client.demo?.status);
  const [savingPhase, setSavingPhase] = useState(false);

  // ── Fas 4 — Publicera och aktivera + katalog-tillval ────────────────
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [togglingCatalog, setTogglingCatalog] = useState(false);

  const setupStatus = client.setup_status || {};
  const testApproved = !!setupStatus.test_approved_at;
  const appearanceApproved = !!setupStatus.appearance_approved_at;
  const basicsOk = !!(client.name && client.email);
  // Kunskapen måste ha kritiska fält (företagsnamn, bransch/roll, scope-källa).
  const knowledgeReady = !!(blueprint.present && blueprint.ready_to_publish);
  const neverPublished = !blueprint.is_published;
  const readyToPublish = basicsOk && knowledgeReady && appearanceApproved && testApproved;
  const publishBlockReasons = [];
  if (!basicsOk) publishBlockReasons.push("Grunduppgifter saknas (namn + e-post)");
  if (!knowledgeReady) publishBlockReasons.push("Kunskapen är ofullständig — kräver företagsnamn, roll/bransch och minst en tjänst/FAQ");
  if (!appearanceApproved) publishBlockReasons.push("Widgetens utseende är inte godkänt");
  if (!testApproved) publishBlockReasons.push("Chatboten är inte testad och godkänd");

  const publishActivate = async () => {
    setPublishing(true);
    setPublishResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${encodeURIComponent(clientId)}/publish-activate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      const d = await res.json();
      setPublishResult(d);
      if (d.success && d.activated) {
        toast.success(d.message || "Publicerad och aktiverad");
      } else {
        toast.error(d.message || "Kunde inte publicera — kunden förblir pausad");
      }
      if (onReload) await onReload();
    } catch (e) {
      toast.error(`Kunde inte publicera: ${e.message}`);
    } finally {
      setPublishing(false);
    }
  };

  const toggleCatalog = async () => {
    setTogglingCatalog(true);
    try {
      await patchClient({ catalog_enabled: !client.catalog_enabled });
      toast.success(!client.catalog_enabled ? "Produktkatalog aktiverad" : "Produktkatalog avstängd");
      if (onReload) await onReload();
    } catch (e) {
      toast.error(`Kunde inte ändra katalogval: ${e.message}`);
    } finally {
      setTogglingCatalog(false);
    }
  };

  const savePhase = async (newStatus) => {
    setSavingPhase(true);
    try {
      // Behåll övriga demo-fält; ändra endast status (kundfas).
      const existing = client.demo || {};
      await patchClient({ demo: { ...existing, status: newStatus } });
      toast.success("Kundfas uppdaterad");
      if (onReload) await onReload();
    } catch (e) {
      toast.error(`Kunde inte ändra kundfas: ${e.message}`);
    } finally {
      setSavingPhase(false);
    }
  };
  const bpPublishedV = blueprint.published_version;
  const bpLatestV = blueprint.version;
  const hasNewerDraft = blueprint.present && bpPublishedV && bpLatestV && bpLatestV > bpPublishedV;

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Grunduppgifter */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h2 className="font-semibold text-gray-900">Grunduppgifter</h2>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${phase.cls}`} data-testid="overview-phase">
              Kundfas: {phase.label}
            </span>
            <select
              value={client.demo?.status || ""}
              disabled={savingPhase}
              onChange={(e) => savePhase(e.target.value || null)}
              data-testid="overview-kundfas-select"
              className="h-7 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-700"
            >
              <option value="">Ej angiven</option>
              <option value="demo">Demo</option>
              <option value="pilot">Pilot</option>
              <option value="active">Kund</option>
              <option value="archived">Arkiverad</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <OvField label="Namn" k="name" value={form.name} onChange={onField("name")} placeholder="Företagsnamn" />
          <OvField label="E-post" k="email" type="email" value={form.email} onChange={onField("email")} placeholder="kund@epost.se" />
          <OvField label="Bransch" k="industry" value={form.industry} onChange={onField("industry")} placeholder="t.ex. Snickare" />
          <OvField label="Ort" k="city" value={form.city} onChange={onField("city")} placeholder="t.ex. Stockholm" />
          <div className="md:col-span-2">
            <OvField label="Hemsida" k="company_url" value={form.company_url} onChange={onField("company_url")} placeholder="https://exempel.se" />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={saveBasics} disabled={saving} data-testid="overview-save-basics">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Spara grunduppgifter
          </Button>
        </div>
      </div>

      {/* Widget På/Pausad + Publicera och aktivera */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold text-gray-900">Widget</div>
            <div className="text-sm text-gray-500">
              Status:{" "}
              <span className={client.active ? "text-emerald-600 font-medium" : "text-gray-500 font-medium"} data-testid="overview-widget-status">
                {client.active ? "På (aktiverad)" : "Pausad"}
              </span>
              . Nya kunder är pausade tills du publicerar och aktiverar.
            </div>
          </div>
          {client.active ? (
            <button
              onClick={toggleWidget}
              disabled={togglingWidget}
              data-testid="overview-widget-toggle"
              className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 bg-amber-100 text-amber-800"
            >
              {togglingWidget ? "…" : "Pausa widget"}
            </button>
          ) : neverPublished ? (
            <span className="text-xs text-gray-400 max-w-[220px] text-right" data-testid="overview-no-manual-activate">
              Nya kunder aktiveras via <strong>Publicera och aktivera</strong> nedan — ingen manuell genväg.
            </span>
          ) : (
            <button
              onClick={toggleWidget}
              disabled={togglingWidget}
              data-testid="overview-widget-toggle"
              className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 bg-gray-100 text-gray-700"
            >
              {togglingWidget ? "…" : "Återaktivera"}
            </button>
          )}
        </div>

        <div className="border-t border-gray-100 pt-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-semibold text-gray-900">Publicera och aktivera</div>
            <div className="text-sm text-gray-500 max-w-xl">
              Publicerar kunskap + utseende, växlar till den gemensamma kärnan och aktiverar widgeten.
              {readyToPublish ? " Allt obligatoriskt är klart." : " Slutför obligatoriska steg först."}
            </div>
          </div>
          <button
            onClick={publishActivate}
            disabled={publishing || !readyToPublish}
            data-testid="overview-publish-activate"
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#255E69] text-white disabled:opacity-40"
          >
            {publishing ? "Publicerar…" : "Publicera och aktivera"}
          </button>
        </div>
        {!readyToPublish && publishBlockReasons.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="overview-publish-blockers">
            <div className="font-medium mb-1">Kan inte publiceras ännu:</div>
            <ul className="list-disc list-inside text-xs space-y-0.5">
              {publishBlockReasons.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
        {publishResult && !publishResult.success && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-testid="overview-publish-error">
            {publishResult.message || "Publicering misslyckades — kunden förblir pausad."}
            {Array.isArray(publishResult.steps) && (
              <ul className="mt-1 list-disc list-inside text-xs">
                {publishResult.steps.filter((s) => s.errors && s.errors.length).map((s, i) => (
                  <li key={i}>{s.step}: {s.errors.join(", ")}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Uppsättningsstatus — checklista mot målbilden */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Uppsättning</h2>
          <p className="text-xs text-gray-500">Status per område. Klicka för att öppna.</p>
        </div>
        <div className="divide-y divide-gray-100">
          <SetupRow
            label="Grunduppgifter granskade"
            status={basicsOk ? "Klart" : "Komplettera"}
            tone={basicsOk ? "ok" : "warn"}
            detail={basicsOk ? null : "Namn och e-post behövs."}
            onClick={() => onTab("overview")}
            testid="setup-row-basics"
          />
          <SetupRow
            label="Kunskap granskad"
            status={blueprint.present ? (blueprint.is_published ? `Publicerad v${bpPublishedV}` : "Utkast — granska") : "Underlag saknas"}
            tone={blueprint.present ? (blueprint.is_published ? "ok" : "neutral") : "warn"}
            detail={
              blueprint.present
                ? (hasNewerDraft ? `Nyare utkast v${bpLatestV} finns opublicerat.` : "Godkänn AI-förslag i AI-agent & kunskap.")
                : "Agentunderlag saknas."
            }
            onClick={() => onTab("knowledge")}
            testid="setup-row-knowledge"
          />
          <SetupRow
            label="Knappar och länkar granskade"
            status="Öppna"
            tone="neutral"
            detail="Godkänn och koppla actions i AI-agent & kunskap."
            onClick={() => onTab("knowledge")}
            testid="setup-row-actions"
          />
          <SetupRow
            label="Widgetens utseende klart"
            status={appearanceApproved ? "Godkänt" : "Ej godkänt"}
            tone={appearanceApproved ? "ok" : "neutral"}
            detail={appearanceApproved ? `Godkänt av ${setupStatus.appearance_approved_by || "admin"}.` : "Konfigurera utseende & meddelanden, markera sedan som klart."}
            onClick={() => onTab("appearance")}
            testid="setup-row-appearance"
          />
          <SetupRow
            label="Chatboten testad och godkänd"
            status={testApproved ? "Godkänt" : "Ej verifierat"}
            tone={testApproved ? "ok" : "neutral"}
            detail={testApproved ? `Godkänt av ${setupStatus.test_approved_by || "admin"}.` : "Öppna testvyn och markera som godkänt."}
            onClick={() => onTab("testmon")}
            testid="setup-row-test"
          />
          {client.catalog_enabled && (
            <SetupRow
              label="Produktdata"
              status="Öppna"
              tone="neutral"
              detail="Katalogkälla, import och publicering."
              onClick={() => onTab("catalog")}
              testid="setup-row-catalog"
            />
          )}
          <SetupRow
            label="Redo att publiceras"
            status={readyToPublish ? "Ja" : "Nej"}
            tone={readyToPublish ? "ok" : "neutral"}
            detail={readyToPublish ? "Använd Publicera och aktivera ovan." : "Slutför obligatoriska steg först."}
            onClick={() => onTab("overview")}
            testid="setup-row-ready"
          />
          <SetupRow
            label="Kundportal"
            status="Tillval"
            tone="neutral"
            detail="Ett avstängt tillval är inte en blockerare."
            onClick={() => onTab("portal")}
            testid="setup-row-portal"
          />
        </div>
      </div>

      {/* Tillval: produktkatalog */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="overview-catalog-optin">
        <div>
          <div className="font-semibold text-gray-900">Produktkatalog (tillval)</div>
          <div className="text-sm text-gray-500 max-w-xl">
            {client.catalog_enabled
              ? "Katalog är på — fliken Produktdata visas i arbetsflödet."
              : "Ingen produktkatalog. De flesta kunder behöver ingen. Slå på för e-handel eller större sortiment."}
          </div>
        </div>
        <button
          onClick={toggleCatalog}
          disabled={togglingCatalog}
          data-testid="overview-catalog-toggle"
          className={`px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 ${client.catalog_enabled ? "bg-amber-100 text-amber-800" : "bg-[#255E69] text-white"}`}
        >
          {togglingCatalog ? "…" : client.catalog_enabled ? "Stäng av katalog" : "Använd produktkatalog"}
        </button>
      </div>
    </div>
  );
}

// Fas 4 — granskningsvänlig lista över föreslagna knappar/länkar (actions).
// Admin ser förslag med källa och kan Godkänna (status=approved + länka till
// blueprint) eller Avvisa. Tekniska ID:n döljs. Godkända actions blir knappar
// i agenten först efter publicering.
function ActionsReviewPanel({ clientId, getAuthHeaders }) {
  const [rows, setRows] = useState(null);
  const [linkedIds, setLinkedIds] = useState([]);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/actions`, {
        credentials: "include", headers: getAuthHeaders(),
      });
      if (!r.ok) { setRows([]); return; }
      const d = await r.json();
      setRows(Array.isArray(d.actions) ? d.actions : []);
      setLinkedIds(d.blueprint_approved_action_ids || []);
    } catch { setRows([]); }
  }, [clientId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const approve = async (row) => {
    if (!row.url || !String(row.url).trim()) {
      toast.error("Länken saknar URL — öppna Avancerat för att fylla i den först.");
      return;
    }
    setBusyId(row.action_id);
    try {
      const up = await fetch(`${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/actions/${encodeURIComponent(row.action_id)}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ status: "approved" }),
      });
      if (!up.ok) throw new Error(`Status ${up.status}`);
      const ln = await fetch(`${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/actions/${encodeURIComponent(row.action_id)}/link`, {
        method: "POST", credentials: "include", headers: getAuthHeaders(),
      });
      if (!ln.ok) throw new Error(`Kunde inte koppla (${ln.status})`);
      toast.success("Länk godkänd och kopplad");
      await load();
    } catch (e) {
      toast.error(`Kunde inte godkänna: ${e.message}`);
    } finally {
      setBusyId("");
    }
  };

  const reject = async (row) => {
    setBusyId(row.action_id);
    try {
      const r = await fetch(`${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/actions/${encodeURIComponent(row.action_id)}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ status: "disabled" }),
      });
      if (!r.ok) throw new Error(`Status ${r.status}`);
      toast.success("Förslag avvisat");
      await load();
    } catch (e) {
      toast.error(`Kunde inte avvisa: ${e.message}`);
    } finally {
      setBusyId("");
    }
  };

  if (rows === null) {
    return <div className="text-sm text-gray-400 py-2" data-testid="actions-review-loading">Laddar förslag…</div>;
  }
  const pending = rows.filter((r) => r.status !== "approved" && r.status !== "disabled");
  const approvedRows = rows.filter((r) => r.status === "approved");

  return (
    <div className="pt-2 border-t border-gray-100" data-testid="knowledge-actions-review">
      <div className="mb-2">
        <h2 className="font-semibold text-gray-900">Knappar och länkar att granska</h2>
        <p className="text-xs text-gray-500">
          AI-föreslagna knappar från kundens sajt. Godkänn för att koppla dem till agenten,
          eller avvisa. Godkända länkar blir klickbara knappar i chatten efter publicering.
        </p>
      </div>

      {pending.length === 0 && (
        <div className="text-sm text-gray-500 bg-gray-50 border border-gray-100 rounded-lg p-3" data-testid="actions-review-empty">
          Inga förslag att granska just nu.
        </div>
      )}

      <div className="space-y-2">
        {pending.map((row) => (
          <div key={row.action_id} className="flex items-start justify-between gap-3 border border-gray-200 rounded-lg p-3" data-testid={`action-review-${row.action_id}`}>
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 break-words">{row.label || row.template_id || "Länk"}</div>
              {row.url ? (
                <a href={row.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#255E69] hover:underline break-all">{row.url}</a>
              ) : (
                <div className="text-xs text-amber-600">URL saknas — fyll i under Avancerat.</div>
              )}
              {row.evidence && <div className="text-[11px] text-gray-400 mt-0.5 break-words">Källa: {row.evidence}</div>}
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" disabled={busyId === row.action_id || !row.url} onClick={() => approve(row)} data-testid={`action-approve-${row.action_id}`} className="bg-[#255E69] text-white">Godkänn</Button>
              <Button size="sm" variant="outline" disabled={busyId === row.action_id} onClick={() => reject(row)} data-testid={`action-reject-${row.action_id}`}>Avvisa</Button>
            </div>
          </div>
        ))}
      </div>

      {approvedRows.length > 0 && (
        <div className="mt-3 text-xs text-gray-500" data-testid="actions-review-approved">
          Godkända ({approvedRows.length}): {approvedRows.map((r) => r.label || r.action_id).join(", ")}
        </div>
      )}
    </div>
  );
}


// Fas 4 — normalvyns editerbara kunskapssektioner. Läser HELA
// client_config från blueprinten och sparar tillbaka HELA configen via den
// kanoniska PUT:en (inga parallella kopior). Strukturell kunskap → Blueprint.
// Källa (source_url) visas där den finns; tekniska ID:n/claim types göms.
function BlueprintKnowledgeSections({ clientId, getAuthHeaders, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}`, {
        credentials: "include", headers: getAuthHeaders(),
      });
      if (!r.ok) { setCfg({}); return; }
      const d = await r.json();
      setCfg((d.blueprint && d.blueprint.client_config) || {});
      setDirty(false);
    } catch { setCfg({}); }
  }, [clientId, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const set = (patch) => { setCfg((c) => ({ ...c, ...patch })); setDirty(true); };
  const linesToArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
  const arrToLines = (a) => (Array.isArray(a) ? a : []).join("\n");

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/client-config`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ client_config: cfg }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail?.message || d.detail || `Status ${r.status}`);
      toast.success("Kunskap sparad");
      setDirty(false);
      if (onSaved) await onSaved();
      await load();
    } catch (e) {
      toast.error(`Kunde inte spara: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (cfg === null) return <div className="text-sm text-gray-400 py-4" data-testid="knowledge-sections-loading">Laddar kunskap…</div>;

  const services = Array.isArray(cfg.services) ? cfg.services : [];
  const faq = Array.isArray(cfg.faq) ? cfg.faq : [];
  const contact = cfg.contact || {};
  const rules = cfg.rules || {};

  const setService = (i, patch) => {
    const next = services.map((s, idx) => idx === i ? { ...s, ...patch } : s);
    set({ services: next });
  };
  const setFaq = (i, patch) => {
    const next = faq.map((f, idx) => idx === i ? { ...f, ...patch } : f);
    set({ faq: next });
  };

  return (
    <div className="space-y-4" data-testid="knowledge-sections">
      {/* Om företaget */}
      <KnowledgeSection title="Om företaget" testid="ksec-company">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <KField label="Företagsnamn"><Input value={cfg.company_name || ""} onChange={(e) => set({ company_name: e.target.value })} data-testid="ksec-company-name" /></KField>
          <KField label="Bransch"><Input value={cfg.industry || ""} onChange={(e) => set({ industry: e.target.value })} data-testid="ksec-industry" /></KField>
          <KField label="Assistentens roll"><Input value={cfg.assistant_role || ""} onChange={(e) => set({ assistant_role: e.target.value })} data-testid="ksec-role" /></KField>
          <KField label="Flödestyp (flow type)"><Input value={cfg.flow_type || ""} onChange={(e) => set({ flow_type: e.target.value })} data-testid="ksec-flowtype" /></KField>
          <div className="md:col-span-2"><KField label="Kort beskrivning"><Textarea rows={2} value={cfg.short_description || ""} onChange={(e) => set({ short_description: e.target.value })} data-testid="ksec-desc" /></KField></div>
        </div>
      </KnowledgeSection>

      {/* Tjänster/produkter */}
      <KnowledgeSection title="Tjänster / produkter" testid="ksec-services"
        onAdd={() => set({ services: [...services, { name: "", description: "", aliases: [], source_url: null }] })}>
        {services.length === 0 && <EmptyHint>Inga tjänster ännu. Lägg till nedan.</EmptyHint>}
        {services.map((s, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`ksec-service-${i}`}>
            <div className="flex gap-2">
              <Input value={s.name || ""} placeholder="Namn" onChange={(e) => setService(i, { name: e.target.value })} data-testid={`ksec-service-name-${i}`} />
              <Button size="sm" variant="outline" onClick={() => set({ services: services.filter((_, idx) => idx !== i) })} data-testid={`ksec-service-remove-${i}`}>Avvisa</Button>
            </div>
            <Textarea rows={2} value={s.description || ""} placeholder="Beskrivning (valfri)" onChange={(e) => setService(i, { description: e.target.value })} data-testid={`ksec-service-desc-${i}`} />
            <SourceLine url={s.source_url} />
          </div>
        ))}
      </KnowledgeSection>

      {/* Områden och målgrupper */}
      <KnowledgeSection title="Områden och målgrupper" testid="ksec-areas">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <KField label="Geografiska områden (en per rad)"><Textarea rows={3} value={arrToLines(cfg.service_areas)} onChange={(e) => set({ service_areas: linesToArr(e.target.value) })} data-testid="ksec-areas-text" /></KField>
          <KField label="Målgrupper (en per rad)"><Textarea rows={3} value={arrToLines(cfg.target_customers)} onChange={(e) => set({ target_customers: linesToArr(e.target.value) })} data-testid="ksec-targets-text" /></KField>
        </div>
      </KnowledgeSection>

      {/* Kontakt */}
      <KnowledgeSection title="Kontakt" testid="ksec-contact">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KField label="Telefon"><Input value={contact.phone || ""} onChange={(e) => set({ contact: { ...contact, phone: e.target.value } })} data-testid="ksec-contact-phone" /></KField>
          <KField label="E-post"><Input value={contact.email || ""} onChange={(e) => set({ contact: { ...contact, email: e.target.value } })} data-testid="ksec-contact-email" /></KField>
          <KField label="Adress"><Input value={contact.address || ""} onChange={(e) => set({ contact: { ...contact, address: e.target.value } })} data-testid="ksec-contact-address" /></KField>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">Öppettider och priser hanteras som verifierade fakta längre ner.</p>
      </KnowledgeSection>

      {/* FAQ */}
      <KnowledgeSection title="Vanliga frågor (FAQ)" testid="ksec-faq"
        onAdd={() => set({ faq: [...faq, { question: "", answer: "", source_url: null }] })}>
        {faq.length === 0 && <EmptyHint>Inga FAQ ännu.</EmptyHint>}
        {faq.map((f, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`ksec-faq-${i}`}>
            <div className="flex gap-2">
              <Input value={f.question || ""} placeholder="Fråga" onChange={(e) => setFaq(i, { question: e.target.value })} data-testid={`ksec-faq-q-${i}`} />
              <Button size="sm" variant="outline" onClick={() => set({ faq: faq.filter((_, idx) => idx !== i) })} data-testid={`ksec-faq-remove-${i}`}>Avvisa</Button>
            </div>
            <Textarea rows={2} value={f.answer || ""} placeholder="Svar" onChange={(e) => setFaq(i, { answer: e.target.value })} data-testid={`ksec-faq-a-${i}`} />
            <SourceLine url={f.source_url} />
          </div>
        ))}
      </KnowledgeSection>

      {/* Regler */}
      <KnowledgeSection title="Regler för agenten" testid="ksec-rules">
        <KField label="Gissa aldrig om (en per rad)"><Textarea rows={3} value={arrToLines(rules.do_not_guess)} onChange={(e) => set({ rules: { ...rules, do_not_guess: linesToArr(e.target.value) } })} data-testid="ksec-rules-donotguess" /></KField>
        <KField label="Kundspecifika noteringar (en per rad)"><Textarea rows={2} value={arrToLines(rules.customer_specific_notes)} onChange={(e) => set({ rules: { ...rules, customer_specific_notes: linesToArr(e.target.value) } })} data-testid="ksec-rules-notes" /></KField>
      </KnowledgeSection>

      <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t border-gray-100 py-3 flex justify-end">
        <Button onClick={save} disabled={saving || !dirty} data-testid="knowledge-save-btn" className="bg-[#255E69] text-white">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}{dirty ? "Spara kunskap" : "Sparat"}
        </Button>
      </div>
    </div>
  );
}

const KnowledgeSection = ({ title, testid, onAdd, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid={testid}>
    <div className="flex items-center justify-between">
      <h3 className="font-semibold text-gray-900">{title}</h3>
      {onAdd && <Button size="sm" variant="outline" onClick={onAdd} data-testid={`${testid}-add`}>+ Lägg till</Button>}
    </div>
    {children}
  </div>
);
const KField = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
const EmptyHint = ({ children }) => <div className="text-sm text-gray-400 italic">{children}</div>;
const SourceLine = ({ url }) => url ? (
  <div className="text-[11px] text-gray-400">Källa: <a href={url} target="_blank" rel="noopener noreferrer" className="text-[#255E69] hover:underline break-all">{url}</a></div>
) : <div className="text-[11px] text-gray-300">Ingen källa angiven</div>;

// Fas 4 — normalvyns granskning av verifierbara påståenden (priser,
// öppettider, villkor) → EvidenceFact. Godkänn skapar + godkänner faktumet
// bakom kulisserna med förslagets claim types (dolda). Avvisa döljer förslaget.
function EvidenceReviewSection({ clientId, getAuthHeaders }) {
  const [suggestions, setSuggestions] = useState(null);
  const [facts, setFacts] = useState([]);
  const [busy, setBusy] = useState("");
  const [dismissed, setDismissed] = useState([]);
  const [edits, setEdits] = useState({});

  const base = `${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}`;
  const load = useCallback(async () => {
    try {
      const [sr, fr] = await Promise.all([
        fetch(`${base}/evidence/suggestions`, { credentials: "include", headers: getAuthHeaders() }),
        fetch(`${base}/evidence`, { credentials: "include", headers: getAuthHeaders() }),
      ]);
      setSuggestions(sr.ok ? ((await sr.json()).suggestions || []) : []);
      if (fr.ok) {
        const fj = await fr.json();
        setFacts(fj.facts || fj.items || []);
      } else {
        setFacts([]);
      }
    } catch { setSuggestions([]); }
  }, [base, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const approveSuggestion = async (s) => {
    setBusy(s.fact_id);
    try {
      const editedText = (edits[s.fact_id] ?? s.value?.text ?? "").trim();
      const draft = {
        fact_id: s.fact_id, subject: s.subject, predicate: s.predicate,
        value: { ...(s.value || {}), text: editedText },
        allowed_claim_types: s.allowed_claim_types,
        // Utan källa attesterar admin faktumet själv (admin_attestation),
        // annars behåll förslagets källtyp.
        source_type: s.source_url ? (s.source_type || "scrape") : "admin_attestation",
        source_url: s.source_url || null,
      };
      const cr = await fetch(`${base}/evidence`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(draft),
      });
      const cd = await cr.json().catch(() => ({}));
      if (!cr.ok) throw new Error(cd.detail || cd.error || `Status ${cr.status}`);
      const factRef = cd.fact_ref || cd.fact?.fact_ref || cd.fact_id || s.fact_id;
      const ap = await fetch(`${base}/evidence/${encodeURIComponent(factRef)}/approve`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ confirm: true }),
      });
      const apd = await ap.json().catch(() => ({}));
      if (!ap.ok) throw new Error(apd.detail || apd.error || `Godkännande misslyckades (${ap.status})`);
      toast.success("Faktum skapat och godkänt");
      await load();
    } catch (e) {
      toast.error(`Kunde inte godkänna: ${e.message}`);
    } finally {
      setBusy("");
    }
  };

  const rejectSuggestion = async (s) => {
    setBusy(s.fact_id);
    try {
      const r = await fetch(`${base}/evidence/suggestions/${encodeURIComponent(s.fact_id)}/dismiss`, {
        method: "POST", credentials: "include", headers: getAuthHeaders(),
      });
      if (!r.ok) throw new Error(`Status ${r.status}`);
      toast.success("Förslag avvisat");
      setDismissed((d) => [...d, s.fact_id]);
      await load();
    } catch (e) {
      toast.error(`Kunde inte avvisa: ${e.message}`);
    } finally {
      setBusy("");
    }
  };

  if (suggestions === null) return <div className="text-sm text-gray-400 py-2">Laddar…</div>;
  const pending = suggestions.filter((s) => !dismissed.includes(s.fact_id) && !facts.some((f) => f.fact_id === s.fact_id));

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="knowledge-evidence-review">
      <div>
        <h3 className="font-semibold text-gray-900">Priser, öppettider och villkor</h3>
        <p className="text-xs text-gray-500">Verifierbara påståenden. Godkänn för att låta agenten uttala dem som sanning. Endast Elido godkänner verifierad kunskap.</p>
      </div>
      {pending.length === 0 && <EmptyHint>Inga nya förslag att granska.</EmptyHint>}
      {pending.map((s) => (
        <div key={s.fact_id} className="border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3" data-testid={`evidence-suggestion-${s.fact_id}`}>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">{s.predicate}</div>
            <Input
              value={edits[s.fact_id] ?? s.value?.text ?? ""}
              onChange={(e) => setEdits((m) => ({ ...m, [s.fact_id]: e.target.value }))}
              className="mt-1 text-sm"
              data-testid={`evidence-edit-${s.fact_id}`}
            />
            {s.note && <div className="text-[11px] text-gray-400 mt-0.5">{s.note}</div>}
            <SourceLine url={s.source_url} />
          </div>
          <div className="flex gap-1.5 shrink-0">
            <Button size="sm" disabled={busy === s.fact_id} onClick={() => approveSuggestion(s)} data-testid={`evidence-approve-${s.fact_id}`} className="bg-[#255E69] text-white">Godkänn</Button>
            <Button size="sm" variant="outline" disabled={busy === s.fact_id} onClick={() => rejectSuggestion(s)} data-testid={`evidence-reject-${s.fact_id}`}>Avvisa</Button>
          </div>
        </div>
      ))}
      {facts.length > 0 && (
        <div className="text-xs text-gray-500 pt-1" data-testid="evidence-existing-facts">
          Verifierade fakta ({facts.length}): {facts.map((f) => `${f.predicate || f.fact_id}${f.status ? ` (${f.status})` : ""}`).join(", ")}
        </div>
      )}
    </div>
  );
}


function KnowledgeTab({ data, getAuthHeaders }) {
  const [reloadKey, setReloadKey] = useState(0);
  if (!data) return null;
  const { blueprint } = data;
  const isBlocked = data?.runtime?.runtime_health === "error"
    || data?.runtime?.active_runtime === "blocked";
  if (!blueprint.present) {
    // Agentunderlag saknas: dölj INTE hela fliken. Verifierade fakta stöds
    // utan Blueprint och renderas nedan; endast blueprint-beroende kontroller
    // (koppling, kunskapsläge, publicering) är otillgängliga med orsak.
    return (
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="knowledge-missing-blueprint">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900">Agentunderlag saknas</h3>
              <p className="text-sm text-amber-800 mt-1">
                Den här kunden har inget Agent Blueprint ännu. Du kan fortfarande
                lägga till och godkänna verifierade fakta nedan. Publicering,
                kunskapsläge och koppling till blueprint blir tillgängliga när
                ett agentunderlag skapats via onboarding.
              </p>
              <Link
                to={`/admin/blueprints/${encodeURIComponent(data.client_id)}`}
                className="inline-flex items-center gap-1.5 mt-3 text-sm text-[#255E69] hover:underline"
                data-testid="knowledge-empty-open-blueprint"
              >
                Öppna agentunderlag (avancerat) <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        <div data-testid="knowledge-verified-facts">
          <div className="mb-2">
            <h2 className="font-semibold text-gray-900">Verifierad kunskap (fakta)</h2>
            <p className="text-xs text-gray-500">
              Godkända, länkade och färska fakta som den gemensamma dialogkärnan
              får uttala som sanning. Stöds även utan agentunderlag.
            </p>
          </div>
          <BlueprintEvidencePanel clientId={data.client_id} getAuthHeaders={getAuthHeaders} />
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {/* Sanningsenlig status: publicerad version vs senaste utkast hålls isär. */}
      <div className={`border rounded-lg p-3 text-sm flex items-start gap-2 ${
        isBlocked ? "bg-red-50 border-red-200 text-red-800" : "bg-sky-50 border-sky-200 text-sky-800"
      }`} data-testid="knowledge-source-banner">
        <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
        <span data-testid="knowledge-source-state">
          {isBlocked ? (
            <>Agentunderlaget är valt men <strong>blockerat</strong> — lös blockeringen innan publicerad kunskap kan användas.</>
          ) : blueprint.is_published ? (
            <>Publicerad version <strong>v{blueprint.published_version}</strong>.{blueprint.version && blueprint.published_version && blueprint.version > blueprint.published_version ? ` Ett nyare utkast (v${blueprint.version}) är opublicerat.` : ""}</>
          ) : (
            <>Ingen publicerad version ännu (senaste utkast v{blueprint.version}). Granska och godkänn nedan, publicera sedan.</>
          )}
        </span>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-semibold text-gray-900">AI-agentens kunskap & regler</h2>
          <p className="text-xs text-gray-500">Granska, redigera och godkänn kundens kunskap. Ändringar sparas i agentunderlaget (v{blueprint.version}).</p>
        </div>
        <StatusBadge value={blueprint.status} />
      </div>

      {blueprint.missing_critical?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm" data-testid="knowledge-missing-critical">
          <div className="font-semibold text-amber-700">Saknade kritiska fält:</div>
          <div className="mt-1 text-amber-700">{blueprint.missing_critical.join(", ")}</div>
        </div>
      )}

      {/* Editerbara kunskapssektioner (strukturell kunskap → Blueprint) */}
      <BlueprintKnowledgeSections
        key={reloadKey}
        clientId={data.client_id}
        getAuthHeaders={getAuthHeaders}
        onSaved={() => setReloadKey((k) => k + 1)}
      />

      {/* Priser/öppettider/villkor (verifierbara påståenden → EvidenceFact) */}
      <EvidenceReviewSection clientId={data.client_id} getAuthHeaders={getAuthHeaders} />

      {/* Knappar och länkar → client actions */}
      <ActionsReviewPanel clientId={data.client_id} getAuthHeaders={getAuthHeaders} />

      {/* Avancerat: tekniska ID:n, claim types, TTL, rå blueprint-JSON */}
      <details className="bg-white border border-gray-200 rounded-xl p-4 group" data-testid="knowledge-advanced">
        <summary className="cursor-pointer select-none font-semibold text-gray-900 flex items-center gap-1.5">
          <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
          Avancerat (tekniska fält)
        </summary>
        <p className="text-xs text-gray-500 mt-1 mb-3">
          Teknisk evidence-editor (fact_id, claim types, TTL, versionering) och rå blueprint-JSON.
          Endast Elido skapar och godkänner verifierad kunskap.
        </p>
        <BlueprintEvidencePanel clientId={data.client_id} getAuthHeaders={getAuthHeaders} />
        <Link
          to={`/admin/blueprints/${encodeURIComponent(data.client_id)}`}
          className="inline-flex items-center gap-1 mt-3 text-xs text-[#255E69] hover:underline"
          data-testid="knowledge-advanced-edit-link"
        >
          Öppna full blueprint-detalj (rå JSON) <ChevronRight className="w-3 h-3" />
        </Link>
      </details>
    </div>
  );
}

// ─── H3.2.3 — Utseende-flik med riktig redigering ──────────────────────
// Denna flik ersätter den tidigare "Skapas via Agent Blueprints"-stub
// och låter admin redigera widgetens utseende direkt från Kund 360 mot
// rätt källa beroende på runtime:
//   • runtime=blueprint  → läs/skriv widget_presentations (PUT /api/admin/blueprints/{cid}/presentation)
//   • runtime=legacy     → läs/skriv handymen-fält (PATCH /api/admin/client/{cid})
//   • saknar presentation → empty state med "Skapa från legacy" + "Skapa tom".
const APPEARANCE_TONES = [
  { value: "", label: "(ej satt)" },
  { value: "professional", label: "Professionell" },
  { value: "friendly", label: "Vänlig" },
  { value: "casual", label: "Avslappnad" },
  { value: "expert", label: "Expert" },
];
const APPEARANCE_POSITIONS = [
  { value: "", label: "(default)" },
  { value: "bottom_right", label: "Bottom right" },
  { value: "bottom_left", label: "Bottom left" },
];

function AppearanceField({ label, hint, children, testid }) {
  return (
    <div className="bg-white border border-gray-100 rounded-lg p-3" data-testid={testid}>
      <label className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function AppearanceTab({ data, onReload, onTab }) {
  const { getAuthHeaders } = useAuth();
  const clientId = data?.client_id;
  const c = data?.client || {};
  const present = !!c.present;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    start_message: c.start_message || "",
    proactive_enabled: !!c.proactive_message,
    proactive_message: c.proactive_message || "",
    proactive_delay_ms: c.proactive_delay_ms ?? 8000,
    accent_color: c.primary_color || "",
    tone: c.tone || "",
    logo_url: c.logo_url || "",
    streaming_enabled: !!c.streaming_enabled,
    streaming_word_delay_ms: c.streaming_word_delay_ms || 160,
  });

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const _safeUrl = (u) => { if (!u) return true; if (/^(javascript|data|vbscript|file):/i.test(u)) return false; return /^(https?:\/\/|\/|#)/i.test(u); };
  const _safeColor = (c) => { if (!c) return true; return /^(#[0-9A-Fa-f]{3,8}|[a-zA-Z]+)$/.test(c.trim()); };
  const urlError = (!_safeUrl(form.logo_url) && "Ogiltig logo URL") || (!_safeColor(form.accent_color) && "Ogiltig färg") || "";

  const save = async () => {
    if (urlError) { toast.error(urlError); return; }
    // Blockera sparning tills laddad kundidentitet matchar routen.
    if (!data || data.client_id !== clientId) {
      toast.error("Kundidentitet matchar inte routen — läser om");
      return;
    }
    setSaving(true);
    try {
      // ENDAST handymen-widgetfält via befintlig PATCH. Ingen samtidig
      // presentation-, demo-, runtime-, pricing- eller kunskapsändring.
      // "" rensar strängfält (backend), false och 0 skickas som giltiga
      // värden (0 coercas inte till null).
      const body = {
        start_message: form.start_message,
        proactive_message: form.proactive_enabled ? form.proactive_message : "",
        proactive_delay_ms: Number(form.proactive_delay_ms),
        primary_color: form.accent_color,
        tone: form.tone,
        logo_url: form.logo_url,
        streaming_enabled: !!form.streaming_enabled,
        // Backend-kontrakt: ge=20, le=400. Clampa så ett giltigt 0-försök
        // inte 422:ar hela PATCH:en (0 är inte ett giltigt värde för detta fält).
        streaming_word_delay_ms: Math.max(20, Math.min(400, Number(form.streaming_word_delay_ms) || 160)),
      };
      const r = await fetch(`${API_URL}/api/admin/client/${encodeURIComponent(clientId)}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || j.error || `Status ${r.status}`);
      toast.success("Widgetinställningar sparade. Ändringar påverkar widgeten direkt.");
      if (onReload) await onReload();
    } catch (e) {
      toast.error(`Kunde inte spara: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (!present) return <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-sm text-gray-500">Kunde inte läsa kunddata.</div>;

  return (
    <div className="space-y-4" data-testid="appearance-tab">
      <SetupApprovalCard
        data={data} clientId={clientId} onReload={onReload}
        approvedKey="appearance_approved_at" byKey="appearance_approved_by"
        approveAction="appearance-approve" resetAction="appearance-reset"
        title="Godkänn utseende"
        description="Bekräfta att widgetens utseende och meddelanden är klara. Krävs innan du kan publicera och aktivera."
        approveLabel="Markera utseende som klart"
        testidPrefix="appearance-approval"
      />
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="appearance-editor">
        <div>
          <h2 className="font-semibold text-gray-900">Widget &amp; utseende</h2>
          <p className="text-xs text-gray-500">Sparas direkt till widgetens fält. Ingen presentation, demo eller runtime ändras här.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <AppearanceField label="Tonprofil" testid="field-tone">
            <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.tone} onChange={(e) => update({ tone: e.target.value })} data-testid="appearance-tone">
              {APPEARANCE_TONES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </AppearanceField>
          <AppearanceField label="Accent / primärfärg" hint="HEX (#0F2A30) eller CSS-color. Töm för att rensa." testid="field-color">
            <div className="flex items-center gap-2">
              <Input value={form.accent_color} onChange={(e) => update({ accent_color: e.target.value })} placeholder="#0F2A30" data-testid="appearance-accent-color" />
              {form.accent_color && <span className="w-7 h-7 rounded border border-gray-200 shrink-0" style={{ backgroundColor: form.accent_color }} />}
            </div>
          </AppearanceField>
          <AppearanceField label="Första meddelande (start_message)" hint="Töm fältet för att rensa." testid="field-start">
            <Textarea rows={2} value={form.start_message} onChange={(e) => update({ start_message: e.target.value })} data-testid="appearance-start-message" />
          </AppearanceField>
          <AppearanceField label="Proaktivt meddelande" hint="Pop-up-bubblan innan chatten öppnats." testid="field-proactive">
            <Textarea rows={2} value={form.proactive_message} onChange={(e) => update({ proactive_message: e.target.value })} data-testid="appearance-proactive-message" />
          </AppearanceField>
          <AppearanceField label="Proaktiv aktiv" testid="field-proactive-enabled">
            <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.proactive_enabled ? "1" : "0"} onChange={(e) => update({ proactive_enabled: e.target.value === "1" })} data-testid="appearance-proactive-enabled">
              <option value="0">Av</option>
              <option value="1">På</option>
            </select>
          </AppearanceField>
          <AppearanceField label="Proaktiv fördröjning (ms)" hint="0 – 120000 ms (0 tillåtet)" testid="field-proactive-delay">
            <Input type="number" min="0" max="120000" value={form.proactive_delay_ms} onChange={(e) => update({ proactive_delay_ms: e.target.value })} data-testid="appearance-proactive-delay" />
          </AppearanceField>
          <AppearanceField label="Logo URL" hint="Töm för att rensa." testid="field-logo">
            <Input value={form.logo_url} onChange={(e) => update({ logo_url: e.target.value })} placeholder="https://elido.nu/logo.png" data-testid="appearance-logo-url" />
          </AppearanceField>
          <AppearanceField label="Streaming-läge" hint="Per-kund streaming (av = false, delay klampas 20–400)." testid="field-streaming">
            <div className="flex items-center gap-3">
              <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.streaming_enabled ? "1" : "0"} onChange={(e) => update({ streaming_enabled: e.target.value === "1" })} data-testid="appearance-streaming-enabled">
                <option value="0">Av</option>
                <option value="1">På</option>
              </select>
              <Input type="number" min="20" max="400" className="w-28" value={form.streaming_word_delay_ms} onChange={(e) => update({ streaming_word_delay_ms: e.target.value })} data-testid="appearance-streaming-delay" />
              <span className="text-[11px] text-gray-500">ms/ord</span>
            </div>
          </AppearanceField>
        </div>
        {urlError && <div className="text-xs text-red-600" data-testid="appearance-guardrail-error">{urlError} — åtgärda innan du sparar.</div>}
        <div className="flex items-center justify-end pt-2 border-t border-gray-100">
          <Button size="sm" onClick={save} disabled={saving || !!urlError} data-testid="appearance-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Spara widgetinställningar
          </Button>
        </div>
      </div>

      {clientId && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2" data-testid="appearance-install-section">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="font-semibold text-gray-900">Installation / widgetkod</h3>
              <p className="text-xs text-gray-500 mt-0.5">Klistra in före stängande &lt;/body&gt; på kundens sajt.</p>
            </div>
            <button type="button" onClick={async () => {
              const snippet = `<script src="https://elido.nu/widget.js" data-client-id="${clientId}" defer></script>`;
              try { await navigator.clipboard.writeText(snippet); toast.success("Widget-snippet kopierad"); } catch { toast.error("Kunde inte kopiera"); }
            }} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#0F2A30] hover:bg-[#255E69] rounded-full" data-testid="appearance-install-copy-btn">
              <Copy className="w-3.5 h-3.5" /> Kopiera snippet
            </button>
          </div>
          <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-x-auto" data-testid="appearance-install-snippet">
{`<script src="https://elido.nu/widget.js" data-client-id="${clientId}" defer></script>`}
          </pre>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4 text-sm text-gray-600 flex flex-wrap gap-4">
        <button onClick={() => onTab?.("testmon")} className="text-[#255E69] hover:underline inline-flex items-center gap-1" data-testid="appearance-goto-sandbox">
          <Beaker className="w-4 h-4" /> Testa i sandbox
        </button>
        <button onClick={() => onTab?.("advanced")} className="text-[#255E69] hover:underline inline-flex items-center gap-1" data-testid="appearance-goto-presentation">
          <Settings className="w-4 h-4" /> Widget Presentation (avancerat)
        </button>
      </div>
    </div>
  );
}

// Demoåtgärder — flyttade till Test. Sparar ENDAST demo{} via klient-PATCH.
function DemoSettingsCard({ data, clientId, onReload }) {
  const { getAuthHeaders } = useAuth();
  const demo = data?.client?.demo || {};
  const initial = {
    demo_enabled: !!demo.enabled,
    demo_sandbox: !!demo.sandbox,
    demo_slug: demo.slug || "",
    demo_headline: demo.headline || "",
    demo_description: demo.description || "",
    demo_test_questions: Array.isArray(demo.test_questions) ? demo.test_questions.join("\n") : "",
    demo_screenshot_url: demo.screenshot_url || "",
  };
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initial);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    // Blockera sparning tills laddad kundidentitet matchar routen.
    if (!data || data.client_id !== clientId) {
      toast.error("Kundidentitet matchar inte routen — läser om");
      return;
    }
    setSaving(true);
    try {
      const tqs = (form.demo_test_questions || "").split(/\n+/).map((q) => q.trim()).filter(Boolean).slice(0, 8);
      // Fullständigt demo-objekt (nested replace). Behåll status (kundfas
      // sätts i Översikt). Avsiktligt tömda fält skickas som null → rensas.
      const body = {
        demo: {
          ...demo,
          enabled: !!form.demo_enabled,
          sandbox: !!form.demo_sandbox,
          slug: (form.demo_slug || "").trim() || null,
          status: demo.status || "demo",
          headline: (form.demo_headline || "").trim() || null,
          description: (form.demo_description || "").trim() || null,
          test_questions: tqs.length > 0 ? tqs : null,
          screenshot_url: (form.demo_screenshot_url || "").trim() || null,
        },
      };
      const r = await fetch(`${API_URL}/api/admin/client/${encodeURIComponent(clientId)}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || j.error || `Status ${r.status}`);
      toast.success("Demo-inställningar sparade");
      if (onReload) await onReload();
    } catch (e) {
      toast.error(`Kunde inte spara demo: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="test-demo-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-semibold text-gray-900">Publik demo-sida</h3>
          <p className="text-xs text-gray-500 mt-0.5">Publik demo på <code>/demo/chatbot/&lt;slug&gt;</code>. Sparar endast demo-fälten.</p>
        </div>
        {form.demo_enabled && form.demo_slug && (
          <a href={`/demo/chatbot/${form.demo_slug}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#255E69] hover:underline inline-flex items-center gap-1" data-testid="test-demo-open-link">
            Öppna demo <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <AppearanceField label="Demo aktiv" testid="field-demo-enabled">
          <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.demo_enabled ? "1" : "0"} onChange={(e) => update({ demo_enabled: e.target.value === "1" })} data-testid="test-demo-enabled">
            <option value="0">Nej</option><option value="1">Ja</option>
          </select>
        </AppearanceField>
        <AppearanceField label="Privat demosandbox" hint="Kör LLM + guards utan lead-/konversationslagring eller mejl." testid="field-demo-sandbox">
          <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.demo_sandbox ? "1" : "0"} onChange={(e) => update({ demo_sandbox: e.target.value === "1" })} data-testid="test-demo-sandbox">
            <option value="0">Nej — normalt demobeteende</option><option value="1">Ja — helt utan sidoeffekter</option>
          </select>
        </AppearanceField>
        <AppearanceField label="Demo-slug" hint="Tomt = auto-genereras vid första aktivering." testid="field-demo-slug">
          <Input value={form.demo_slug} onChange={(e) => update({ demo_slug: e.target.value })} placeholder="ex: elido" data-testid="test-demo-slug" />
        </AppearanceField>
        <AppearanceField label="Demo-rubrik" testid="field-demo-headline">
          <Input value={form.demo_headline} onChange={(e) => update({ demo_headline: e.target.value })} placeholder="Testa vår AI-agent" data-testid="test-demo-headline" />
        </AppearanceField>
        <div className="md:col-span-2">
          <AppearanceField label="Demo-beskrivning" testid="field-demo-description">
            <Textarea rows={2} value={form.demo_description} onChange={(e) => update({ demo_description: e.target.value })} data-testid="test-demo-description" />
          </AppearanceField>
        </div>
        <div className="md:col-span-2">
          <AppearanceField label="Testfrågor (en per rad, max 8)" testid="field-demo-test-questions">
            <Textarea rows={3} value={form.demo_test_questions} onChange={(e) => update({ demo_test_questions: e.target.value })} placeholder="Vad kostar det?&#10;Hur snabbt kan ni börja?" data-testid="test-demo-test-questions" />
          </AppearanceField>
        </div>
        <div className="md:col-span-2">
          <AppearanceField label="Bakgrunds-screenshot (URL)" hint="Publik bild-URL." testid="field-demo-screenshot-url">
            <Input value={form.demo_screenshot_url} onChange={(e) => update({ demo_screenshot_url: e.target.value })} placeholder="https://…/screenshot.png" data-testid="test-demo-screenshot-url" />
          </AppearanceField>
        </div>
      </div>
      <div className="flex items-center justify-end pt-2 border-t border-gray-100">
        <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid="test-save-demo">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Spara demo-inställningar
        </Button>
      </div>
    </div>
  );
}

// Widget Presentation — specialistverktyg under Avancerat. Skapa/spara/publicera
// presentation. Rör aldrig runtime_source; ingen automatisk publish.
function WidgetPresentationPanel({ data, onReload }) {
  const { getAuthHeaders } = useAuth();
  const clientId = data?.client_id;
  const isBlueprint = data?.runtime?.active_runtime === "blueprint";
  const legacyClient = data?.client || {};
  const [presentation, setPresentation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ display_name: "", tone: "", position: "", avatar_url: "" });

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const presRes = await fetch(`${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/presentation`, { credentials: "include", headers: getAuthHeaders() }).catch(() => null);
      let pres = null;
      if (presRes && presRes.ok) { const j = await presRes.json(); pres = j.presentation || null; }
      setPresentation(pres);
      if (pres) setForm({ display_name: pres.display_name || "", tone: pres.tone || "", position: pres.position || "", avatar_url: pres.avatar_url || "" });
    } catch (e) {
      toast.error("Kunde inte ladda presentation");
    } finally { setLoading(false); }
  }, [clientId, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const putPresentation = async (body, okMsg) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/presentation`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || j.error || "Misslyckades");
      toast.success(okMsg);
      await load(); onReload?.();
    } catch (e) { toast.error(e.message || "Kunde inte spara presentation"); }
    finally { setBusy(false); }
  };

  const save = () => putPresentation({
    presentation: {
      tone: form.tone || "professional",
      display_name: form.display_name,
      position: form.position || null,
      avatar_url: form.avatar_url || null,
      // Chrome bevaras från befintlig presentation (ägs av handymen/Utseende).
      start_message: presentation?.start_message || "",
      proactive_message: presentation?.proactive_message || "",
      proactive_enabled: !!presentation?.proactive_enabled,
      proactive_delay_ms: presentation?.proactive_delay_ms ?? 0,
      accent_color: presentation?.accent_color || null,
      logo_url: presentation?.logo_url || null,
    },
  }, "Widget Presentation sparad (review_required). Publicera för blueprint-runtime.");

  const createFromLegacy = () => putPresentation({
    presentation: {
      tone: legacyClient?.tone || "professional",
      display_name: legacyClient?.name || "",
      start_message: legacyClient?.start_message || "",
      proactive_message: legacyClient?.proactive_message || "",
      proactive_enabled: !!(legacyClient?.proactive_message),
      proactive_delay_ms: legacyClient?.proactive_delay_ms || 8000,
      accent_color: legacyClient?.primary_color || null,
      logo_url: legacyClient?.logo_url || null,
      position: null, avatar_url: null,
    },
  }, "Widget Presentation skapad från legacy (review_required).");

  const publish = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/blueprints/${encodeURIComponent(clientId)}/presentation/publish`, {
        method: "POST", credentials: "include", headers: { ...getAuthHeaders() },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || j.error || "Misslyckades");
      toast.success(j.message || (j.noop ? "Redan publicerad." : "Widget Presentation publicerad"));
      await load(); onReload?.();
    } catch (e) { toast.error(e.message || "Kunde inte publicera"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="bg-white border border-gray-200 rounded-xl p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="advanced-presentation">
      <div>
        <h3 className="font-semibold text-gray-900">Widget Presentation (blueprint)</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Presentationsspecifika fält som hör till den gemensamma kärnans
          agentunderlag. Att spara eller publicera här ändrar <strong>inte</strong>
          vilken runtime som körs — runtimeval sker separat och är inget som
          styrs från den här panelen.
        </p>
      </div>
      {!presentation ? (
        <div className="border border-dashed border-gray-300 rounded-lg p-4" data-testid="advanced-presentation-empty">
          <p className="text-sm text-gray-500 mb-1">Ingen widgetpresentation finns ännu.</p>
          <p className="text-xs text-amber-600 mb-3">Migrationsverktyg för äldre kunder. Nya kunder får en presentation automatiskt vid skapandet — det här behövs bara för kunder som skapats innan det.</p>
          <Button size="sm" variant="outline" onClick={createFromLegacy} disabled={busy || !legacyClient} data-testid="advanced-presentation-create">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}Skapa från nuvarande inställningar
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <StatusBadge value={presentation.status} />
            <span className="text-gray-500">v{presentation.version}</span>
            {presentation.published_at && <span className="text-gray-400">publicerad {new Date(presentation.published_at).toLocaleString("sv-SE")}</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <AppearanceField label="Visningsnamn" testid="pres-field-display-name">
              <Input value={form.display_name} onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))} data-testid="advanced-presentation-display-name" />
            </AppearanceField>
            <AppearanceField label="Tonprofil" testid="pres-field-tone">
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.tone} onChange={(e) => setForm((f) => ({ ...f, tone: e.target.value }))} data-testid="advanced-presentation-tone">
                {APPEARANCE_TONES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </AppearanceField>
            <AppearanceField label="Position" testid="pres-field-position">
              <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.position} onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))} data-testid="advanced-presentation-position">
                {APPEARANCE_POSITIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </AppearanceField>
            <AppearanceField label="Avatar URL" testid="pres-field-avatar">
              <Input value={form.avatar_url} onChange={(e) => setForm((f) => ({ ...f, avatar_url: e.target.value }))} data-testid="advanced-presentation-avatar" />
            </AppearanceField>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <Button size="sm" variant="outline" onClick={save} disabled={busy} data-testid="advanced-presentation-save">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Spara presentation
            </Button>
            <Button size="sm" onClick={publish} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="advanced-presentation-publish">
              Publicera presentation
            </Button>
          </div>
        </>
      )}
    </div>
  );
}



function MonitorTab({ data }) {
  if (!data) return null;
  const { monitor, client_id } = data;
  const Stat = ({ label, value, hint, color = "text-gray-900" }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-semibold text-gray-900">Statistik / Monitor (24h)</h2>
          <p className="text-xs text-gray-500">PII-säker observability — inga råa meddelanden lagras.</p>
        </div>
        <Link to={`/admin/runtime-events?client_id=${encodeURIComponent(client_id)}`} className="text-xs text-[#255E69] hover:underline inline-flex items-center gap-1">
          Full monitor <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Events" value={monitor.total_events || 0} />
        <Stat label="Blueprint" value={(monitor.by_runtime || {}).blueprint || 0} color="text-emerald-700" />
        <Stat label="Legacy" value={(monitor.by_runtime || {}).legacy || 0} color="text-slate-700" />
        <Stat label="Fallbacks" value={monitor.fallback_count || 0} color={monitor.fallback_count > 0 ? "text-amber-700" : "text-gray-900"} />
        <Stat label="Fel" value={monitor.error_count || 0} color={monitor.error_count > 0 ? "text-red-700" : "text-gray-900"} />
        <Stat label="LLM-anrop" value={monitor.llm_called_count || 0} />
        <Stat label="Leads skapade" value={monitor.leads_finalised_count || 0} color="text-emerald-700" />
        <Stat label="Latens p95" value={`${monitor.p95_latency_ms || 0} ms`} color={monitor.p95_latency_ms > 5000 ? "text-amber-700" : "text-gray-900"} />
      </div>
    </div>
  );
}

function TestTab({ data, onReload, onTab }) {
  const clientId = data?.client_id;
  const clientName = data?.client?.name || clientId || "";
  const [sandbox, setSandbox] = useState(false);
  if (!clientId) return null;
  return (
    <div className="space-y-4" data-testid="test-tab">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="font-semibold text-gray-900">Testa widgeten (sandbox)</h2>
        <p className="text-sm text-gray-500 mt-1">
          Kör hela LLM-flödet för <strong>{clientName}</strong> precis som i produktion —
          men inga leads sparas, inga mejl skickas och inga log-events skapas.
        </p>
        <Button className="mt-4" onClick={() => setSandbox(true)} data-testid="test-open-sandbox">
          <Beaker className="w-4 h-4 mr-1.5" /> Öppna sandbox
        </Button>
      </div>

      <TestApprovalCard data={data} clientId={clientId} onReload={onReload} />

      <DemoSettingsCard data={data} clientId={clientId} onReload={onReload} />

      <div className="bg-white border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
        Shadow Replay, statistik/monitor och teknisk diagnostik finns under{" "}
        <button onClick={() => onTab?.("advanced")} className="text-[#255E69] hover:underline" data-testid="test-goto-advanced">Avancerat</button>.
      </div>

      {sandbox && (
        <WidgetPreviewModal clientId={clientId} clientName={clientName} onClose={() => setSandbox(false)} />
      )}
    </div>
  );
}

// Fas 4 — generiskt kort för explicita uppsättningsgodkännanden
// (test / utseende). Öppnandet av en vy räknas aldrig som godkänt.
function SetupApprovalCard({
  data, clientId, onReload, approvedKey, byKey,
  approveAction, resetAction, title, description, approveLabel, testidPrefix,
}) {
  const { getAuthHeaders } = useAuth();
  const [busy, setBusy] = useState(false);
  const setup = data?.client?.setup_status || {};
  const approved = !!setup[approvedKey];

  const call = async (action) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/clients/${encodeURIComponent(clientId)}/setup/${action}`, {
        method: "POST", credentials: "include", headers: getAuthHeaders(),
      });
      if (!r.ok) throw new Error(`Status ${r.status}`);
      toast.success(action === resetAction ? "Godkännande återställt" : "Markerat som klart");
      if (onReload) await onReload();
    } catch (e) {
      toast.error(`Kunde inte uppdatera status: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between gap-3 flex-wrap" data-testid={`${testidPrefix}-card`}>
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          Status:{" "}
          <span className={approved ? "text-emerald-600 font-medium" : "text-gray-500 font-medium"} data-testid={`${testidPrefix}-status`}>
            {approved ? `Godkänt av ${setup[byKey] || "admin"}` : "Ej verifierat"}
          </span>
          . {description}
        </p>
      </div>
      {approved ? (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => call(resetAction)} data-testid={`${testidPrefix}-reset-btn`}>
          Ångra godkännande
        </Button>
      ) : (
        <Button size="sm" disabled={busy} onClick={() => call(approveAction)} data-testid={`${testidPrefix}-approve-btn`} className="bg-[#255E69] text-white">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}{approveLabel}
        </Button>
      )}
    </div>
  );
}

// Fas 4 — låt admin markera ett genomfört test som godkänt. Öppnandet av
// testvyn räknas aldrig som godkänt; detta är ett explicit val.
function TestApprovalCard({ data, clientId, onReload }) {
  return (
    <SetupApprovalCard
      data={data} clientId={clientId} onReload={onReload}
      approvedKey="test_approved_at" byKey="test_approved_by"
      approveAction="test-approve" resetAction="test-reset"
      title="Godkänn test"
      description="Krävs innan du kan publicera och aktivera."
      approveLabel="Markera test som godkänt"
      testidPrefix="test-approval"
    />
  );
}

function LegacyTab({ data, onReload }) {
  const { getAuthHeaders } = useAuth();
  const [showLegacyModal, setShowLegacyModal] = useState(false);
  const [rawClient, setRawClient] = useState(null);

  const clientId = data?.client_id;

  // H3.3 iter2 — pre-fetch raw handymen doc so EditClientModal (som
  // förväntar sig hela handymen-objektet inklusive pricing JSON och
  // demo-subdoc) har all data när admin öppnar modalen. Cache tills
  // modal öppnas för att undvika onödigt anrop vid tab-mount.
  useEffect(() => {
    let cancelled = false;
    if (!showLegacyModal || !clientId || rawClient) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/client/list`, {
          credentials: "include", headers: getAuthHeaders(),
        });
        if (!r.ok) throw new Error("Kunde inte ladda klient");
        const j = await r.json();
        const me = (j.clients || []).find((c) => c.client_id === clientId);
        if (!cancelled && me) setRawClient(me);
      } catch (e) {
        if (!cancelled) toast.error(e.message || "Kunde inte ladda legacy-data");
      }
    })();
    return () => { cancelled = true; };
  }, [showLegacyModal, clientId, rawClient, getAuthHeaders]);

  if (!data) return null;
  const { client } = data;
  return (
    <div className="space-y-4">
      <details className="bg-white border border-gray-200 rounded-xl" data-testid="legacy-full-editor-panel">
        <summary className="px-5 py-4 cursor-pointer font-semibold text-gray-900 select-none">
          Djupa legacy-/specialistfält (avancerat)
        </summary>
        <div className="px-5 pb-5 text-sm">
          <p className="text-xs text-gray-500 mb-3">
            För djupare legacy-fält som inte finns under Översikt eller Widget &amp;
            utseende: prislista (JSON), prioriterade kvalificeringsfält, tillåten
            domän och rescrape-trigger. Vanliga fält (namn, e-post, bransch, ort,
            hemsida) redigeras under Översikt — inte här.
          </p>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
            <div><dt className="text-xs text-gray-500">Tillåten domän</dt><dd className="text-gray-900">{client.allowed_domain || "—"}</dd></div>
            <div><dt className="text-xs text-gray-500">Klientens hemsida</dt><dd className="text-gray-900">{client.company_url || "—"}</dd></div>
          </dl>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowLegacyModal(true)}
            data-testid="legacy-open-full-editor-btn"
          >
            <Pencil className="w-4 h-4 mr-1.5" />
            Öppna full legacy-editor
          </Button>
        </div>
      </details>

      {showLegacyModal && rawClient && (
        <EditClientModal
          client={rawClient}
          onClose={() => setShowLegacyModal(false)}
          onSaved={() => { setShowLegacyModal(false); toast.success("Legacy-fält sparade"); onReload?.(); }}
        />
      )}
    </div>
  );
}

function SettingsTab({ data, onReload }) {
  const { getAuthHeaders } = useAuth();
  const [actBusy, setActBusy] = useState(false);
  if (!data) return null;
  const { runtime, client_id } = data;

  const flipRuntime = async (target) => {
    setActBusy(true);
    try {
      const path = target === "blueprint" ? "activate" : "rollback";
      const r = await fetch(
        `${API_URL}/api/admin/runtime/${encodeURIComponent(client_id)}/${path}`,
        { method: "POST", credentials: "include", headers: getAuthHeaders() }
      );
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || d.error || "Misslyckades");
      toast.success(`Runtime → ${d.active_runtime || d.runtime_source}`);
      onReload?.();
    } catch (e) {
      toast.error(e.message || "Kunde inte växla runtime");
    } finally {
      setActBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-2">Runtime-källa</h3>
        <p className="text-sm text-gray-500">
          Nuvarande källa: <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">{runtime.runtime_source}</code>
          {" · "}aktiv runtime: <strong>{runtime.active_runtime}</strong>
          {" · "}allowlistad: <strong>{runtime.allowlisted ? "ja" : "nej"}</strong>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Äldre kärna (handymen) är en fullvärdig runtime som kör chatten — inte
          bara ett fallback. Runtime växlas endast via knapparna nedan; ingen
          annan panel ändrar runtime automatiskt.
        </p>
        {runtime.allowlisted && (
          <div className="flex gap-2 mt-4">
            <Button
              size="sm"
              variant={runtime.active_runtime === "blueprint" ? "outline" : "default"}
              disabled={actBusy || runtime.active_runtime === "blueprint"}
              onClick={() => flipRuntime("blueprint")}
              data-testid="settings-activate-blueprint"
            >
              {actBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Aktivera Blueprint
            </Button>
            <Button
              size="sm"
              variant={runtime.active_runtime !== "blueprint" ? "outline" : "default"}
              disabled={actBusy || runtime.active_runtime !== "blueprint"}
              onClick={() => flipRuntime("handymen")}
              data-testid="settings-rollback-legacy"
            >
              {actBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Rulla tillbaka till Legacy
            </Button>
          </div>
        )}
        {!runtime.allowlisted && (
          <p className="text-xs text-amber-700 mt-2">
            Klienten är inte allowlistad för blueprint-runtime. Aktivering kräver att <code>BLUEPRINT_RUNTIME_ALLOWLIST</code> uppdateras.
          </p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 text-sm">
        <h3 className="font-semibold text-gray-900 mb-2">Avancerat / teknisk debug</h3>
        <ul className="space-y-1 text-xs text-gray-600">
          <li>· Runtime monitor: <Link to={`/admin/runtime-events?client_id=${encodeURIComponent(client_id)}`} className="text-[#255E69] hover:underline">öppna</Link></li>
          <li>· Agent Blueprints (JSON-editor): <Link to="/admin/blueprints" className="text-[#255E69] hover:underline">öppna</Link></li>
          <li>· LLM-diagnostik: <Link to="/admin/llm-diagnostik" className="text-[#255E69] hover:underline">öppna</Link></li>
          <li>· Gamla klienter-vyn: <Link to="/admin/clients" className="text-[#255E69] hover:underline">öppna</Link></li>
        </ul>
      </div>
    </div>
  );
}

function LeadsAndConversationsTab({ data }) {
  const [sub, setSub] = useState("requests");
  const clientId = data?.client_id;
  if (!clientId) return null;
  const TabBtn = ({ id, label, testid }) => (
    <button
      type="button"
      onClick={() => setSub(id)}
      data-testid={testid}
      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
        sub === id ? "bg-[#0F2A30] text-white" : "text-gray-600 hover:text-gray-900"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div className="space-y-4" data-testid="leads-conversations">
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1" data-testid="leads-subtabs">
        <TabBtn id="requests" label="Förfrågningar" testid="leads-subtab-requests" />
        <TabBtn id="conversations" label="Samtal" testid="leads-subtab-conversations" />
      </div>
      {sub === "requests" ? (
        <div data-testid="leads-requests-pane">
          {/* Riktig kundfiltrerad ChatbotLeads. Kundscopad via prop — faller
              ALDRIG tillbaka till global /admin/leads. */}
          <ChatbotLeads clientId={clientId} />
        </div>
      ) : (
        <div data-testid="leads-conversations-pane" className="space-y-2">
          <p className="text-xs text-gray-500">Samtal (widget_leads): partial/kvalificerad/slutförd, med kundresa och full historik.</p>
          <SilentLeadsList clientId={clientId} />
        </div>
      )}
    </div>
  );
}

function TestAndStatsTab({ data, onReload, onTab }) {
  if (!data) return null;
  return <TestTab data={data} onReload={onReload} onTab={onTab} />;
}

// Kund-ID + radering. Radering flyttad hit från kundlistan (arbetsorder §5).
function DangerZone({ data }) {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const clientId = data?.client_id;
  const name = data?.client?.name || clientId;
  const [busy, setBusy] = useState(false);
  const del = async () => {
    if (!window.confirm(`Ta bort ${name}? Leads och konversationer behålls i databasen.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/client/${encodeURIComponent(clientId)}`, {
        method: "DELETE", credentials: "include", headers: getAuthHeaders(),
      });
      if (!r.ok) throw new Error(`Status ${r.status}`);
      toast.success(`${name} togs bort`);
      navigate("/admin/clients");
    } catch (e) {
      toast.error(`Kunde inte ta bort: ${e.message}`);
      setBusy(false);
    }
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3" data-testid="advanced-danger-zone">
      <div>
        <h3 className="font-semibold text-gray-900">Kund-ID &amp; radering</h3>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <code className="font-mono text-xs bg-gray-100 px-2 py-1 rounded" data-testid="advanced-client-id">{clientId}</code>
          <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(clientId); toast.success("Client ID kopierat"); } catch { toast.error("Kunde inte kopiera"); } }} className="inline-flex items-center gap-1 text-xs text-[#255E69] hover:underline" data-testid="advanced-copy-client-id">
            <Copy className="w-3.5 h-3.5" /> Kopiera
          </button>
        </div>
      </div>
      <div className="border-t border-gray-100 pt-3">
        <Button size="sm" variant="outline" onClick={del} disabled={busy} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="advanced-delete-client">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}Ta bort kund
        </Button>
      </div>
    </div>
  );
}

// Avancerat — samlar specialistfunktioner: runtime, agentunderlag/Blueprint,
// Widget Presentation, Approved Actions, statistik/diagnostik/Shadow Replay,
// legacy JSON, kund-ID + radering. Inga automatiska runtime-/publish-anrop.
function AdvancedTab({ data, onReload, onTab }) {
  if (!data) return null;
  const clientId = data?.client_id;
  return (
    <div className="space-y-6" data-testid="advanced-tab">
      <SettingsTab data={data} onReload={onReload} />

      <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="advanced-specialist-links">
        <h3 className="font-semibold text-gray-900 mb-2">Specialistverktyg</h3>
        <ul className="space-y-1.5 text-sm">
          <li>· <button onClick={() => onTab?.("advanced", "actions")} className="text-[#255E69] hover:underline" data-testid="advanced-open-actions">Sparade knappar och länkar (Approved Actions)</button></li>
          <li>· <Link to={`/admin/blueprints/${encodeURIComponent(clientId)}`} className="text-[#255E69] hover:underline" data-testid="advanced-open-blueprint">Agentunderlag / Blueprint-editor (JSON)</Link></li>
          <li>· <Link to="/admin/blueprints" className="text-[#255E69] hover:underline" data-testid="advanced-open-replay">Shadow Replay &amp; kvalitetstest</Link></li>
          <li>· <Link to={`/admin/runtime-events?client_id=${encodeURIComponent(clientId)}`} className="text-[#255E69] hover:underline" data-testid="advanced-open-runtime-events">Runtime-events / diagnostik</Link></li>
          <li>· <Link to="/admin/llm-diagnostik" className="text-[#255E69] hover:underline" data-testid="advanced-open-llm">LLM-diagnostik</Link></li>
        </ul>
      </div>

      <WidgetPresentationPanel data={data} onReload={onReload} />

      <MonitorTab data={data} />

      <LegacyTab data={data} onReload={onReload} />

      <DangerZone data={data} />
    </div>
  );
}


export default function ClientDashboard() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const loadTokenRef = useRef(null);

  // URL-styrda flikar via React Router. Bakåt/framåt, reload och direktlänk
  // återställer rätt flik. Legacy-alias mappar gamla kundspecifika flik-id
  // till rätt Kund 360-sektion (inga parallella arbetsytor). Okänd flik → Översikt.
  const rawTab = searchParams.get("tab") || "overview";
  const rawSection = searchParams.get("section") || "";
  // Legacy deep-link-alias.
  const LEGACY_TAB_ALIAS = {
    actions: ["advanced", "actions"],
    settings: ["advanced", ""],
    legacy: ["advanced", ""],
    monitor: ["advanced", ""],
    runtime: ["advanced", ""],
    presentation: ["advanced", ""],
    test: ["testmon", ""],
    stats: ["testmon", ""],
    demo: ["testmon", ""],
    conversations: ["leads", ""],
  };
  let resolvedTab = rawTab;
  let resolvedSection = rawSection;
  if (LEGACY_TAB_ALIAS[rawTab]) {
    [resolvedTab, resolvedSection] = LEGACY_TAB_ALIAS[rawTab];
  }
  if (!TABS.some((t) => t.id === resolvedTab)) {
    resolvedTab = "overview";
  }

  const setTab = useCallback((id, section) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("tab", id);
      if (section) p.set("section", section);
      else p.delete("section");
      return p;
    });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // Race-skydd: ignorera sena svar från en tidigare kund.
    const requestedId = clientId;
    loadTokenRef.current = requestedId;
    try {
      const r = await fetch(
        `${API_URL}/api/admin/clients/${encodeURIComponent(clientId)}/overview`,
        { credentials: "include", headers: getAuthHeaders() }
      );
      if (loadTokenRef.current !== requestedId) return; // kunden bytte under tiden
      if (!r.ok) {
        const txt = await r.text();
        if (loadTokenRef.current !== requestedId) return;
        setLoadError({ status: r.status, message: txt.slice(0, 200) });
        setData(null);
        return;
      }
      const d = await r.json();
      if (loadTokenRef.current !== requestedId) return;
      setData(d);
    } catch (e) {
      if (loadTokenRef.current !== requestedId) return;
      setLoadError({ status: 0, message: String(e.message || e) });
      setData(null);
    } finally {
      if (loadTokenRef.current === requestedId) setLoading(false);
    }
  }, [clientId, getAuthHeaders]);

  // Nollställ kundbundet tillstånd direkt vid kundbyte så gammal kunds data
  // aldrig visas medan nästa laddar.
  useEffect(() => {
    setData(null);
    setLoadError(null);
    setLoading(true);
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  // Fas 4 — dölj Produktdata-fliken i normalflödet när katalog är avstängd.
  const visibleTabs = TABS.filter(
    (t) => t.id !== "catalog" || !!data?.client?.catalog_enabled,
  );

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="kund360">
      {/* Gemensam kundheader — alltid synlig, i alla flikar. */}
      <Kund360Header
        clientId={clientId}
        data={data}
        onBack={() => navigate("/admin/clients")}
        onRefresh={load}
        refreshing={loading}
      />

      {/* Responsiv flikstrip. Mobil = dropdown, ≥sm = wrappande strip. */}
      <div className="mb-6">
        <div className="sm:hidden">
          <select
            value={resolvedTab}
            onChange={(e) => setTab(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-medium bg-white"
            data-testid="kund360-tabs-mobile"
          >
            {visibleTabs.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block border-b border-gray-200" data-testid="kund360-tabs">
          <div className="flex flex-wrap gap-x-1 gap-y-0 -mb-px">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              const isActive = resolvedTab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? "border-[#255E69] text-[#255E69]"
                      : "border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-200"
                  }`}
                  data-testid={`kund360-tab-${t.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content. Produktdata (catalog) är frikopplad från kundöversikten och
          renderas ALLTID med kund-ID — även om overview saknas/fel/legacy. */}
      {resolvedTab === "catalog" ? (
        <CatalogPanel clientId={clientId} getAuthHeaders={getAuthHeaders} />
      ) : loading && !data ? (
        <AdminLoading label="Laddar Kund 360…" />
      ) : loadError ? (
        <AdminError
          what={`Kund 360 för ${clientId}`}
          status={loadError.status}
          onRetry={load}
          onBack={() => navigate("/admin/clients")}
          backLabel="Till Kunder"
        />
      ) : !data ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Kunden kunde inte hittas.</p>
        </div>
      ) : (
        <div key={clientId}>
          {resolvedTab === "overview"   && <OverviewTab data={data} clientId={clientId} onTab={setTab} getAuthHeaders={getAuthHeaders} onReload={load} />}
          {resolvedTab === "leads"      && <LeadsAndConversationsTab data={data} />}
          {resolvedTab === "portal"     && <PortalTab clientId={clientId} getAuthHeaders={getAuthHeaders} />}
          {resolvedTab === "knowledge"  && <KnowledgeTab data={data} getAuthHeaders={getAuthHeaders} />}
          {resolvedTab === "appearance" && <AppearanceTab data={data} onReload={load} onTab={setTab} />}
          {resolvedTab === "testmon"    && <TestAndStatsTab data={data} onReload={load} onTab={setTab} />}
          {resolvedTab === "advanced"   && (
            resolvedSection === "actions"
              ? <ApprovedActionsManager clientId={clientId} isBlueprint={data?.runtime?.active_runtime === "blueprint"} />
              : <AdvancedTab data={data} onReload={load} onTab={setTab} />
          )}
        </div>
      )}
    </div>
  );
}
